import { BadInputError } from '@watershed/errors/BadInputError';
import { UserInputError } from '@watershed/errors/UserInputError';
import { ASTCall, stringifyExpr } from '@watershed/lscript/ast';
import { evalExpression } from '../eval';
import { ExprType, stringifyType } from '@watershed/lscript/expr_types';
import { extractStringLiteral } from '../literal';
import { exprToSql } from '../sql';
import {
  assertArgShape,
  typecheckExpression,
  TypecheckWarning,
} from '../typecheck';
import { Context, Func, SqlExpression } from '../types';

export const not_null: Func = {
  typecheck(
    ctx: Context,
    call: ASTCall,
    warnings: Array<TypecheckWarning>
  ): ExprType {
    assertArgShape(call, 1, 2);

    const argType = typecheckExpression(ctx, call.args[0], warnings);

    if (call.args[1]) {
      const messageType = typecheckExpression(ctx, call.args[1], warnings);
      if (messageType.ty !== 'string') {
        throw new BadInputError(
          `Expected message argument to not_null to be string, but got ${stringifyType(
            messageType
          )}`
        );
      }
    }

    if (argType.ty === 'null') {
      throw new BadInputError("Can't use not_null on a null literal");
    } else if (argType.ty === 'nullable') {
      return argType.baseTy;
    } else {
      return argType;
    }
  },

  eval(ctx: Context, call: ASTCall): unknown {
    const value = evalExpression(ctx, call.args[0]);

    if (value === null) {
      const errorMessage = call.args[1]
        ? extractStringLiteral(call.args[1])
        : `Expected non-null value from expression: ${stringifyExpr(
            call.args[0]
          )}`;
      throw new UserInputError(errorMessage);
    }

    return value;
  },

  toSql(ctx: Context, call: ASTCall): SqlExpression {
    // We don't currently have any mechanism for bubbling up an error of this kind in SQL
    // so not_null() currently exists only for typechecking purposes. :( But that need is
    // meaningful enough that I don't want to delay adding it to the typechecker.

    // TODO: figure out how to do this
    return exprToSql(ctx, call.args[0]);
  },
};
